import { FormattedMessage } from 'react-intl';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import { Link as RouterLink } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { ProjectDocType, TimeEntryDocType } from '@ast-data/shared';
import { projectLabelText } from '../projects/projectLabelText';
import { useActorLabel } from '../users/useActorLabel';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { formatDateTime } from '../../utils/formatDate';
import DoubleBookingDetail from './DoubleBookingDetail';
import { type TimeEntryVariant } from './timeEntryVariant';
import { useShownJson } from '../users/personLabels';

/**
 * The booking that refused a save, in full — and, above all, WHO to go to about
 * it.
 *
 * The conflict is regularly on a Maßnahme the person entering the hours has
 * nothing to do with: a Teamleitung books a colleague's day and learns the
 * colleague is already booked elsewhere. The office editor is no way there —
 * the route sits behind `access WorkerTimeTracking` (App.tsx), and even with it
 * `update TimeEntry` is bound to their own projects. Reads, though, are global
 * for every signed-in user (see the shared ability), so this dialog simply
 * SHOWS the entry — the same table the Abgleich uses, plus the two things that
 * turn a dead end into a next step: who recorded it, and who leads that
 * Maßnahme.
 *
 * The editor is still offered, but only to someone the office route would let
 * in, and in a NEW TAB — the form behind this dialog holds unsaved input.
 */
export interface TimeEntryConflictDialogProps {
    /** The entry already occupying those hours. */
    overlap: TimeEntryDocType;
    variant: TimeEntryVariant;
    open: boolean;
    onClose: () => void;
}

export default function TimeEntryConflictDialog({
    overlap,
    variant,
    open,
    onClose
}: TimeEntryConflictDialogProps) {
    const ability = useAbility();
    const actorLabel = useActorLabel();
    const projectsQuery = useStableQuery<ProjectDocType>({});
    const { results: projectsDocs } = useLiveRxQuery<ProjectDocType>({
        collection: 'projects',
        query: projectsQuery
    });
    const projects = useShownJson<ProjectDocType>('project', projectsDocs);
    const project = (projects as ProjectDocType[]).find(
        (candidate) => candidate.id === overlap.projectId
    );
    const projectName = project ? projectLabelText(project) : '';

    // Only offer the editor to someone the route would let through — otherwise
    // the link leads to the "no access" wall this dialog exists to replace.
    const mayOpenEditor = ability.can('access', variant.accessSubject);
    // Whether they could change the entry even so — the ordinary case being
    // two rows of their own crew colliding.
    const mayEdit = ability.can(
        'update',
        scopedSubject('TimeEntry', { projectId: overlap.projectId })
    );

    const recordedBy = actorLabel(overlap.createdBy);
    const changedBy = actorLabel(overlap.updatedBy);
    // Only worth a second line when it says something the first does not.
    const showChangedBy =
        !!changedBy && overlap.updatedBy !== overlap.createdBy;
    // Whom to call about the other Maßnahme. Technical lead first — that is the
    // person running the excavation the hours were booked on.
    const leads = [
        ...(project?.technicalDirector ?? []),
        ...(project?.scientificDirector ?? [])
    ].filter((name) => name.trim().length > 0);

    /**
     * Whether any of the three name lines above renders. All can be absent at
     * once — an imported row with no `createdBy` on a Massnahme that names no
     * lead — and the "tell the people named above" note must not then point at
     * an empty space.
     */
    const hasContacts = !!recordedBy || showChangedBy || leads.length > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            // Two of the three notices that open this live inside a SNACKBAR,
            // which outranks a dialog in MUI's z-index scale — without this it
            // would open behind the message that offered it.
            sx={{ zIndex: (theme) => theme.zIndex.snackbar + 1 }}
        >
            <DialogTitle>
                <FormattedMessage id="timeEntry.conflict.dialogTitle" />
            </DialogTitle>
            <DialogContent>
                {/* The same table as the Abgleich and the row's issue panel.
                    The date is shown because this row stands alone, with no
                    second one next to it to place it in the day. */}
                <DoubleBookingDetail
                    entries={[
                        {
                            id: overlap.id,
                            date: overlap.date,
                            startTime: overlap.startTime,
                            endTime: overlap.endTime,
                            pauseMinutes: overlap.pauseMinutes,
                            note: overlap.note,
                            project: projectName || '—'
                        }
                    ]}
                    titleId="timeEntry.conflict.otherEntry"
                    helpId={variant.conflictHelpId}
                    showDate
                />
                <Box sx={{ px: 1, pt: 1 }}>
                    <Typography variant="body2">
                        <FormattedMessage
                            id="timeEntry.conflict.worker"
                            values={{
                                label: (
                                    <FormattedMessage
                                        id={variant.nameLabelId}
                                    />
                                ),
                                name: overlap.workerName || '—'
                            }}
                        />
                    </Typography>
                    {recordedBy && (
                        <Typography variant="body2">
                            <FormattedMessage
                                id="timeEntry.conflict.recordedBy"
                                values={{
                                    name: recordedBy,
                                    when: formatDateTime(overlap.createdAt)
                                }}
                            />
                        </Typography>
                    )}
                    {showChangedBy && (
                        <Typography variant="body2">
                            <FormattedMessage
                                id="timeEntry.conflict.changedBy"
                                values={{
                                    name: changedBy,
                                    when: formatDateTime(overlap.updatedAt)
                                }}
                            />
                        </Typography>
                    )}
                    {leads.length > 0 && (
                        <Typography variant="body2">
                            <FormattedMessage
                                id="timeEntry.conflict.projectLead"
                                values={{ names: leads.join(', ') }}
                            />
                        </Typography>
                    )}
                </Box>
                {!mayEdit && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <FormattedMessage
                            id={
                                hasContacts
                                    ? 'timeEntry.conflict.notYours'
                                    : 'timeEntry.conflict.notYoursNoContact'
                            }
                            values={{ project: projectName || '—' }}
                        />
                    </Alert>
                )}
                {/*
                    May change it, but may not open the office editor — exactly
                    a Teamleitung (Junior): scoped `update TimeEntry`, no
                    `access WorkerTimeTracking` (see ability.ts). The note above
                    stays silent and no button appears, so without this line
                    they get neither an action nor a reason. The Feldaufnahme's
                    Zeiterfassung is open to them (see fieldCategories).
                */}
                {mayEdit && !mayOpenEditor && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <FormattedMessage id="timeEntry.conflict.editInField" />
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                {mayOpenEditor && (
                    <Button
                        variant="contained"
                        component={RouterLink}
                        to={`${variant.officePath}/${overlap.id}/edit`}
                        target="_blank"
                        rel="noopener"
                        endIcon={<OpenInNewIcon />}
                    >
                        <FormattedMessage id="timeEntry.overlapOpen" />
                    </Button>
                )}
                <Button variant="outlined" onClick={onClose}>
                    <FormattedMessage id="common.close" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
